"use client";

import { useState, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { Icon } from "@/components/ui/Icon";
import Link from "next/link";
import { motion, AnimatePresence } from "framer-motion";
import { Vehicle } from "@/data/vehicles";
import { contactEmail, contactPhone } from "@/config/infos";
import type { Locale } from "@/config/i18n";
import {
  getVehicleById,
  getVehicleIdFromSearchParams,
  getVehicleLabelById,
  getVehicleOptions,
  LOCATION_BOOKING_FORM_ID,
} from "../../components/locationBooking";
import { VEHICLES_UI } from "../../components/vehiclesUi";
import { FICHE_UI } from "./ficheUi";
import { tr } from "@/config/localeText";
import {
  formatEuro,
  getRentalPriceTiers,
  RENTAL_DURATION_OPTIONS,
  type RentalDurationTierKey,
} from "../../components/pricing";
import { getAttribution } from "@/lib/attribution-client";

// Types
type ContactFormData = {
  name: string;
  phone: string;
  email: string;
  vehicleId: string;
  rentalDurationTier: RentalDurationTierKey;
  date: string;
  message: string;
  honeypot?: string;
};

type ContactCarProps = {
  vehicle: Vehicle;
  locale?: Locale;
};

const SectionHeader = ({ locale }: { locale: Locale }) => {
  const t = tr(FICHE_UI, locale).contact;
  return (
    <div className="text-center">
      <h2 className="text-3xl font-light uppercase text-primaryLoc">
        {t.heading}
      </h2>
      <p className="mt-2 text-lg text-primaryLoc/80">{t.subheading}</p>
    </div>
  );
};

const FormField = ({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) => (
  <div className="relative space-y-1.5">
    <label className="text-sm text-primaryLoc/80">{label}</label>
    {children}
    <div className="h-[1px] bg-primaryLoc/20 transition-colors group-focus-within:bg-primaryLoc" />
    <AnimatePresence>
      {error && (
        <motion.p
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          className="text-xs text-red-500"
          role="alert"
        >
          {error}
        </motion.p>
      )}
    </AnimatePresence>
  </div>
);

const ContactInfo = () => (
  <div className="space-y-4 text-primaryLoc/75">
    <Link
      href={`tel:${contactPhone.replace(/\s/g, "")}`}
      className="flex items-center gap-2 transition-colors hover:text-primaryLoc"
    >
      <Icon icon="mdi:phone" className="h-5 w-5" />
      <span>{contactPhone}</span>
    </Link>
    <Link
      href={`mailto:${contactEmail}`}
      className="flex items-center gap-2 transition-colors hover:text-primaryLoc"
    >
      <Icon icon="mdi:email" className="h-5 w-5" />
      <span>{contactEmail}</span>
    </Link>
  </div>
);

const SuccessToast = ({
  onClose,
  locale,
}: {
  onClose: () => void;
  locale: Locale;
}) => (
  <motion.div
    initial={false}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: 50 }}
    className="fixed bottom-4 right-4 z-50 flex items-center gap-2 rounded-lg bg-green-50 px-4 py-3 text-green-800 shadow-lg"
  >
    <Icon icon="mdi:check-circle" className="h-5 w-5" />
    <p>{tr(FICHE_UI, locale).contact.successToast}</p>
    <button
      onClick={onClose}
      className="ms-2 rounded-full p-1 hover:bg-green-100"
      aria-label={tr(FICHE_UI, locale).contact.close}
    >
      <Icon icon="mdi:close" className="h-4 w-4" />
    </button>
  </motion.div>
);

export default function ContactCar({
  vehicle,
  locale = "fr",
}: ContactCarProps) {
  const t = tr(FICHE_UI, locale).contact;
  const durationLabels = tr(VEHICLES_UI, locale).durationLabels;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [firstFocusTime, setFirstFocusTime] = useState<number | null>(null);
  const vehicleOptions = useMemo(() => getVehicleOptions(), []);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    reset,
  } = useForm<ContactFormData>({
    mode: "onSubmit",
    defaultValues: {
      vehicleId: vehicle.id,
      rentalDurationTier: "oneToThreeDays",
      date: "",
    },
  });

  const selectedVehicleId = watch("vehicleId");
  const selectedRentalDurationTier = watch("rentalDurationTier");
  const selectedVehicle = useMemo(
    () => getVehicleById(selectedVehicleId) || vehicle,
    [selectedVehicleId, vehicle],
  );
  const selectedVehicleTiers = useMemo(
    () => getRentalPriceTiers(selectedVehicle),
    [selectedVehicle],
  );
  const selectedDailyPrice = selectedVehicleTiers
    ? selectedVehicleTiers[selectedRentalDurationTier]
    : null;

  useEffect(() => {
    const handleFocus = () => {
      if (!firstFocusTime) {
        setFirstFocusTime(Date.now());
      }
    };

    document.addEventListener("focus", handleFocus, true);
    return () => document.removeEventListener("focus", handleFocus, true);
  }, [firstFocusTime]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const fromQuery = getVehicleIdFromSearchParams(params.get("vehicle"));
    setValue("vehicleId", fromQuery || vehicle.id, { shouldDirty: false });
  }, [setValue, vehicle.id]);

  const onSubmit = async (data: ContactFormData) => {
    if (
      data.honeypot ||
      !firstFocusTime ||
      Date.now() - firstFocusTime < 3000
    ) {
      return;
    }

    setIsSubmitting(true);
    setShowError(false);
    setErrorMessage(null);

    try {
      const selectedVehicle = getVehicleById(data.vehicleId) || vehicle;
      const vehicleLabel =
        getVehicleLabelById(data.vehicleId) ||
        `${selectedVehicle.name} (${selectedVehicle.brand})`;
      const rentalTiers = getRentalPriceTiers(selectedVehicle);
      const rentalDailyPrice = rentalTiers
        ? rentalTiers[data.rentalDurationTier]
        : null;
      const [firstName = data.name, ...rest] = data.name.trim().split(/\s+/);
      const lastName = rest.join(" ") || firstName;
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          attribution: getAttribution(),
          firstName,
          lastName,
          name: data.name,
          email: data.email,
          phone: data.phone,
          subject: `${t.subjectPrefix}${vehicleLabel}`,
          formType: "renseignements",
          quoteRequested: true,
          locationType: "location_vehicule_detail",
          vehicle: data.vehicleId,
          vehicleId: data.vehicleId,
          rentalDurationTier: data.rentalDurationTier,
          rentalDailyPrice: rentalDailyPrice ?? undefined,
          date: data.date || undefined,
          sourcePage: window.location.href,
          locale,
          message: data.message,
          honeypot: data.honeypot || "",
        }),
      });

      const responseData = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(
          typeof responseData?.error === "string"
            ? responseData.error
            : t.error,
        );
      }

      reset({
        name: "",
        phone: "",
        email: "",
        vehicleId: data.vehicleId,
        rentalDurationTier: data.rentalDurationTier,
        date: "",
        message: "",
      });
      setShowSuccess(true);
      setShowError(false);
      setTimeout(() => setShowSuccess(false), 5000);
    } catch (error: unknown) {
      setShowError(true);
      setErrorMessage(error instanceof Error ? error.message : t.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id={LOCATION_BOOKING_FORM_ID} className="bg-white py-14 md:py-20">
      <div className="mx-auto max-w-[90rem] px-6 md:px-8">
        <SectionHeader locale={locale} />

        <div className="mt-6 rounded-xl border border-primaryLoc/15 bg-secondary/35 p-4 text-primaryLoc">
          <p className="text-sm">
            {t.selectedModel}{" "}
            <span className="font-semibold">
              {getVehicleLabelById(selectedVehicleId) ||
                `${vehicle.name} (${vehicle.brand})`}
            </span>
          </p>
          {selectedVehicleTiers && (
            <div className="mt-3 grid gap-2 text-xs md:grid-cols-3">
              {RENTAL_DURATION_OPTIONS.map((option) => {
                const isSelected = option.value === selectedRentalDurationTier;
                return (
                  <div
                    key={option.value}
                    className={`rounded-md border px-2 py-1.5 ${
                      isSelected
                        ? "border-primaryLoc/50 bg-white"
                        : "border-primaryLoc/20 bg-secondary/20"
                    }`}
                  >
                    <p className="text-[10px] uppercase tracking-[0.02em] text-primaryLoc/70">
                      {durationLabels[option.value]}
                    </p>
                    <p className="font-semibold text-primaryLoc">
                      {formatEuro(selectedVehicleTiers[option.value])}
                      {t.perDay}
                    </p>
                  </div>
                );
              })}
            </div>
          )}
          {selectedDailyPrice && (
            <p className="mt-2 text-xs font-medium text-primaryLoc">
              {t.priceForDuration} {formatEuro(selectedDailyPrice)}
              {t.perDay}
            </p>
          )}
        </div>

        <div className="mt-10 grid gap-8 lg:mt-12 lg:grid-cols-3 lg:gap-10">
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="group lg:col-span-2"
            aria-labelledby="contact-form-title"
          >
            <div className="grid gap-8 rounded-2xl border border-primaryLoc/10 bg-secondary/40 p-5 md:grid-cols-2 md:p-6">
              <div className="space-y-8">
                <FormField label={t.fullName} error={errors.name?.message}>
                  <input
                    type="text"
                    className="min-h-11 w-full bg-transparent py-2 text-primaryLoc outline-none placeholder:text-primaryLoc/45"
                    {...register("name", {
                      required: t.v.nameReq,
                      pattern: {
                        value: /^[a-zA-Z\u00C0-\u00FF\s-]{2,80}$/,
                        message: t.v.nameInvalid,
                      },
                    })}
                  />
                </FormField>

                <FormField label={t.phone} error={errors.phone?.message}>
                  <input
                    type="tel"
                    inputMode="tel"
                    className="min-h-11 w-full bg-transparent py-2 text-primaryLoc outline-none placeholder:text-primaryLoc/45"
                    {...register("phone", {
                      required: t.v.phoneReq,
                      pattern: {
                        value: t.phonePattern,
                        message: t.v.phoneInvalid,
                      },
                    })}
                  />
                </FormField>

                <FormField label={t.email} error={errors.email?.message}>
                  <input
                    type="email"
                    className="min-h-11 w-full bg-transparent py-2 text-primaryLoc outline-none placeholder:text-primaryLoc/45"
                    {...register("email", {
                      required: t.v.emailReq,
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: t.v.emailInvalid,
                      },
                    })}
                  />
                </FormField>
              </div>

              <div className="space-y-8">
                <FormField label={t.vehicle} error={errors.vehicleId?.message}>
                  <select
                    className="min-h-11 w-full bg-white py-2 text-primaryLoc outline-none"
                    {...register("vehicleId", {
                      required: t.v.vehicleReq,
                    })}
                  >
                    <option value="" className="text-primaryLoc/65">
                      {t.selectModel}
                    </option>
                    {vehicleOptions.map((option) => (
                      <option
                        key={option.id}
                        value={option.id}
                        className="text-primaryLoc"
                      >
                        {option.label}
                      </option>
                    ))}
                  </select>
                </FormField>

                <FormField
                  label={t.duration}
                  error={errors.rentalDurationTier?.message}
                >
                  <select
                    className="min-h-11 w-full bg-white py-2 text-primaryLoc outline-none"
                    {...register("rentalDurationTier", {
                      required: t.v.durationReq,
                    })}
                  >
                    {RENTAL_DURATION_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {durationLabels[option.value]}
                      </option>
                    ))}
                  </select>
                </FormField>

                <FormField label={t.date} error={errors.date?.message}>
                  <input
                    type="date"
                    className="min-h-11 w-full bg-transparent py-2 text-primaryLoc outline-none"
                    {...register("date")}
                  />
                </FormField>

                <FormField label={t.message} error={errors.message?.message}>
                  <textarea
                    rows={4}
                    className="w-full resize-none bg-transparent py-2 text-primaryLoc outline-none placeholder:text-primaryLoc/45"
                    {...register("message", {
                      required: t.v.messageReq,
                      minLength: { value: 10, message: t.v.messageShort },
                      maxLength: { value: 1000, message: t.v.messageLong },
                    })}
                  />
                </FormField>
              </div>
            </div>

            <input
              type="text"
              className="hidden"
              tabIndex={-1}
              {...register("honeypot")}
            />

            <div className="mt-8 flex flex-col items-start gap-3 md:flex-row md:items-center md:justify-end">
              <button
                type="submit"
                disabled={isSubmitting}
                className="inline-flex min-h-11 items-center justify-center rounded-full border border-primaryLoc px-6 py-2 text-sm font-medium text-primaryLoc transition-colors hover:bg-primaryLoc hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isSubmitting ? (
                  <>
                    <Icon
                      icon="mdi:loading"
                      className="me-2 h-5 w-5 animate-spin"
                    />
                    {t.sending}
                  </>
                ) : (
                  t.submit
                )}
              </button>
              {showError && (
                <p className="text-sm text-red-600" role="alert">
                  {errorMessage || t.error}
                </p>
              )}
            </div>
          </form>

          <div>
            <ContactInfo />
          </div>
        </div>
      </div>

      <AnimatePresence>
        {showSuccess && (
          <SuccessToast onClose={() => setShowSuccess(false)} locale={locale} />
        )}
      </AnimatePresence>
    </section>
  );
}
